import React, { useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import SpecializationTrackConfig from '../components/SpecializationTrackConfig';
import GateEditor from '../components/specialization/GateEditor';
import LearnersPanel from '../components/specialization/LearnersPanel';
import LearnerDrawer from '../components/specialization/LearnerDrawer';
import {
  CARD,
  PRIMARY,
  SECONDARY,
} from '../components/specialization/specText';
import { specError, useGate } from '../hooks/useSpecialization';
import { canUpdateCohort, canUpdateStudent } from '../utils/permissionKeys';

function Settings({ cohortId, cohortName, canSeeLearners }) {
  const { data, isLoading, error, refetch, isFetching } = useGate(cohortId);
  const [tracksOpen, setTracksOpen] = useState(false);
  // Tracks decide the core modules and the specializations on offer, so
  // refresh the settings' module list after editing them.
  const closeTracks = () => {
    setTracksOpen(false);
    refetch();
  };
  if (isLoading) {
    return (
      <div className={`${CARD} h-64 animate-pulse`} aria-label="Loading" />
    );
  }
  if (error) {
    return (
      <div className={`${CARD} p-8 text-center space-y-3`}>
        <p className="text-sm text-gray-600">
          {specError(error, "We couldn't load the qualification settings.")}
        </p>
        {error?.response?.status >= 500 && (
          <button type="button" onClick={() => refetch()} className={PRIMARY}>
            Retry
          </button>
        )}
      </div>
    );
  }
  return (
    <>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setTracksOpen(true)}
          className={SECONDARY}
        >
          Edit specialization tracks
        </button>
      </div>
      <GateEditor
        cohortId={cohortId}
        gate={data}
        canSeeLearners={canSeeLearners}
        onReload={() => !isFetching && refetch()}
        onEditTracks={() => setTracksOpen(true)}
      />
      <Modal
        modalOpen={tracksOpen}
        onClose={closeTracks}
        title={`Specialization Tracks - ${cohortName}`}
      >
        <SpecializationTrackConfig cohortId={cohortId} />
      </Modal>
    </>
  );
}

// Per-cohort rules a learner must meet before choosing a specialization,
// and support tools for individual learners. Settings need UpdateCohort;
// the learner tools need UpdateStudent.
export default function SpecializationQualification() {
  const { cohortStatus, cohortName, cohortId } = useParams();
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const role = useSelector((state) => state.auth.userDetails?.role);
  const canSettings = canUpdateCohort(permissions, role);
  const canLearners = canUpdateStudent(permissions, role);
  const [params, setParams] = useSearchParams();
  const [learner, setLearner] = useState(null);
  // Only staff who can read the settings know the reset option.
  const { data: gate } = useGate(canSettings ? cohortId : null);

  const cohortPath = `/cohorts=${cohortStatus}/${cohortName}/${cohortId}`;
  if (!canSettings && !canLearners) return <Navigate to={cohortPath} replace />;

  const tabs = [
    canSettings && { key: 'settings', label: 'Qualification settings' },
    canLearners && { key: 'learners', label: 'Learners' },
  ].filter(Boolean);
  const tab = tabs.some((t) => t.key === params.get('tab'))
    ? params.get('tab')
    : tabs[0].key;

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <header className="space-y-1">
          <Link
            to={cohortPath}
            className="text-xs text-claret-500 hover:underline"
          >
            ← {cohortName}
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">
            Specialization qualification
          </h1>
          <p className="text-xs text-gray-500">
            What learners in {cohortName} must achieve before they can choose a
            specialization, and help for individual learners. Specialization
            tracks themselves are edited under Specialization Tracks.
          </p>
        </header>

        {tabs.length > 1 && (
          <nav className="flex flex-wrap gap-1 border-b border-gray-200">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setParams({ tab: t.key }, { replace: true })}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                  tab === t.key
                    ? 'border-claret-500 text-claret-500'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        )}

        {tab === 'settings' && (
          <Settings
            cohortId={cohortId}
            cohortName={cohortName}
            canSeeLearners={canLearners}
          />
        )}
        {tab === 'learners' && (
          <LearnersPanel cohortId={cohortId} onOpen={setLearner} />
        )}
      </div>

      <LearnerDrawer
        cohortId={cohortId}
        learner={learner}
        keepStarted={gate?.config?.keep_started_on_reset}
        onClose={() => setLearner(null)}
      />
    </MainContent>
  );
}
